import { buttonUiTemplate } from "../frontViews/buttonUiTemplate";
import type Button from "../../../src/models/Button";
import type { BaseButtonProps } from "../../../src/models/BaseDeviceProps";
import { channelTypeInfo } from "../../../src/models/ChannelType";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import { getEloItemProfileForInstance, getRoomEloItemProfile } from "../data/C1_eloItemsRegistry";
import type { C1_Project } from "../model/C1_Project";
import type {
  C1_ButtonProperties,
  C1_EloItem_Definition,
  C1_EloItem_Details,
  C1_EloItem_Data,
} from "../types/C1_EloTypes";
import { C1_EloItem_PlugType, C1_EloItem_Type, createDefaultButtonProperties } from "../types/C1_EloTypes";
import { getButtonProductDefinition } from "../data/C1_ProductDefinitions";
import { materializedRootForEloOwner } from "../model/C1_eloOwnerEditing";

type C1_ReportSourceType = "room" | "furniture" | "window" | "door" | "elo";

export type C1_ReportConsumer = {
  id: string;
  tag?: string;
  definitionId: string;
  description?: string;
  consumerType: C1_EloItem_Type | "window_shading";
  count?: number;
  details?: Partial<C1_EloItem_Details> | { shading: string };
  sourceType?: C1_ReportSourceType;
  sourceId?: string;
  direction?: number;
  belongsToRoomName?: string;
  groupDisplayOnly?: boolean;
  /**
   * ID des ersten Verbrauchers einer gemeinsam verdrahteten und gemeinsam
   * geschalteten Leuchtengruppe. Die Referenz wird in den Berichtsdaten ab der
   * zweiten Leuchte automatisch erzeugt; die erste Leuchte selbst besitzt
   * keine Referenz. Tags sind dafÃ¼r ungeeignet, weil sie editierbar sind â€“ die
   * stabile Objekt-ID beschreibt die fachliche ZusammengehÃ¶rigkeit eindeutig.
   */
  wiredTogetherWith?: string;
};

export type C1_ReportButton = {
  id: string;
  tag?: string;
  definitionId: string;
  description?: string;
  count?: number;
  details?: C1_EloItem_Details;
  properties?: C1_ButtonProperties;
  sourceType?: C1_ReportSourceType;
  sourceId?: string;
  direction?: number;
  belongsToRoomName?: string;
  groupDisplayOnly?: boolean;
};

export type C1_ReportGroup = {
  id: string;
  /** UrsprÃ¼ngliche ELO-Definitions-ID, bevor sourceId auf den Besitzer gesetzt wird. */
  definitionId?: string;
  tag?: string;
  name?: string;
  description?: string;
  sourceType: C1_ReportSourceType;
  sourceId: string;
  direction?: number;
  consumers: C1_ReportConsumer[];
  buttons: C1_ReportButton[];
  groups: C1_ReportGroup[];
};

export type C1_ReportRoom = {
  id: string;
  tag?: string;
  fullTag?: string;
  name: string;
  consumers: C1_ReportConsumer[];
  sensors: C1_ReportConsumer[];
  buttons: C1_ReportButton[];
  groups: C1_ReportGroup[];
};

export type C1_ReportFloor = {
  id: string;
  tag?: string;
  fullTag?: string;
  name: string;
  level: number;
  rooms: C1_ReportRoom[];
};

export type C1_ProjectReportData = {
  id: string;
  tag: "project";
  name: string;
  floors: C1_ReportFloor[];
};

type C1_ReportItem = C1_ReportConsumer | C1_ReportButton | C1_ReportGroup;
type C1_ReportContainer = Pick<C1_ReportGroup, "consumers" | "buttons" | "groups">;
type DescriptionTemplateContext = Record<string, unknown>;

function emptyContainer(): C1_ReportContainer {
  return { consumers: [], buttons: [], groups: [] };
}

function addItem(container: C1_ReportContainer, item: C1_ReportItem): void {
  if ("groups" in item) container.groups.push(item);
  else if ("consumerType" in item) container.consumers.push(item);
  else container.buttons.push(item);
}

function itemCount(container: C1_ReportContainer): number {
  return container.consumers.length + container.buttons.length + container.groups.length;
}

function onlyItem(container: C1_ReportContainer): C1_ReportItem | undefined {
  if (itemCount(container) !== 1) return undefined;
  return container.consumers[0] ?? container.buttons[0] ?? container.groups[0];
}

function collectGroupItems(groups: C1_ReportGroup[]): Pick<C1_ReportContainer, "consumers" | "buttons"> {
  const consumers: C1_ReportConsumer[] = [];
  const buttons: C1_ReportButton[] = [];

  const collect = (group: C1_ReportGroup) => {
    consumers.push(...group.consumers);
    buttons.push(...group.buttons.filter((item) => !item.groupDisplayOnly));
    group.groups.forEach(collect);
  };

  groups.forEach(collect);
  return { consumers, buttons };
}

/** Plain 230 V outlets in one outlet group share one supply circuit. */
function markPlugGroupWiring(container: C1_ReportContainer): void {
  const plugs = container.consumers.filter((item) => {
    if (item.consumerType !== C1_EloItem_Type.Plug) return false;
    const details = item.details && !("shading" in item.details) ? item.details : undefined;
    return details?.plugType === C1_EloItem_PlugType.V230;
  });
  const firstPlugId = plugs[0]?.id;
  plugs.forEach((plug, index) => {
    plug.wiredTogetherWith = index > 0 ? firstPlugId : undefined;
  });
}

function definitionForInstance(
  instance: C1_EloItem_Data,
  definitions: C1_EloItem_Definition[],
): C1_EloItem_Definition | undefined {
  const definitionId = instance.definitionId ?? "";
  const exact = definitions.find((definition) => definition.id === definitionId);
  if (exact) return exact;

  // Repeated ELO definitions receive an instance suffix such as "-1".
  const baseId = definitionId.replace(/-\d+$/, "");
  return definitions.find((definition) => definition.id === baseId);
}

function itemType(instance: C1_EloItem_Data, definition?: C1_EloItem_Definition): C1_EloItem_Type | undefined {
  if (definition?.type) return definition.type;
  if (instance.details?.plugType) return C1_EloItem_Type.Plug;
  if (instance.details?.channelType && channelTypeInfo(instance.details.channelType).isLight) return C1_EloItem_Type.Light;
  if (instance.details?.buttonType || instance.details?.numberOfButtonChannels) return C1_EloItem_Type.Button;
  if (instance.details?.voltage) return C1_EloItem_Type.FixedPower;
  if (instance.details?.sensorTypes) return C1_EloItem_Type.Sensor;
  return undefined;
}

function cleanDetails(details?: C1_EloItem_Details): Partial<C1_EloItem_Details> | undefined {
  if (!details) return undefined;
  const definedEntries = Object.entries(details).filter(([, value]) => value !== undefined);
  return definedEntries.length ? (Object.fromEntries(definedEntries) as Partial<C1_EloItem_Details>) : undefined;
}

function mapEloInstance(
  instance: C1_EloItem_Data,
  definitions: C1_EloItem_Definition[],
  parentDefinitionIds: string[] = [],
  descriptionParts: string[] = [],
  templateContext: DescriptionTemplateContext = {},
): C1_ReportItem | undefined {
  const definition = definitionForInstance(instance, definitions);
  const type = itemType(instance, definition);
  const hasChildren = (instance.items?.length ?? 0) > 0;
  // Dynamisch erzeugte Items wie die einzelnen Leuchten einer Leuchtengruppe
  // besitzen bewusst keine editierbare Profildefinition. Ihre persistierten
  // technischen Details bestimmen den Typ trotzdem eindeutig.
  if (!definition && !hasChildren && !type) return undefined;
  const definitionIds = [...parentDefinitionIds, instance.definitionId].filter(Boolean);
  const hierarchicalDefinitionId = definitionIds.join(",");
  const nestedDescriptionParts = descriptionParts;
  const displayNameResolver = templateContext.eloDisplayName;
  const description =
    typeof displayNameResolver === "function"
      ? ((displayNameResolver as (id: string) => string | undefined)(instance.id) ?? "")
      : "";

  if (type === C1_EloItem_Type.Group || hasChildren) {
    const content = mapEloInstances(
      instance.items ?? [],
      definition?.items ?? [],
      definitionIds,
      nestedDescriptionParts,
      templateContext,
    );
    const liftedItem = onlyItem(content);
    if (definition?.type === C1_EloItem_Type.PlugGroup) markPlugGroupWiring(content);
    if (liftedItem && definition?.material?.role !== "product") return liftedItem;

    return {
      id: instance.id,
      definitionId: instance.definitionId,
      ...(instance.tag !== undefined ? { tag: instance.tag } : {}),
      ...(description ? { name: description } : {}),
      ...(description ? { description } : {}),
      sourceType: "elo",
      sourceId: instance.definitionId,
      ...content,
    };
  }

  if (!type) return undefined;

  if (type === C1_EloItem_Type.Button) {
    const resolveBaseProps = templateContext.buttonBaseProps as ((id: string) => BaseButtonProps | undefined) | undefined;
    const baseProps = resolveBaseProps?.(instance.id);
    const details = cleanDetails(baseProps ? { ...instance.details, buttonType: buttonUiTemplate(baseProps.type) } : instance.details);
    const properties = baseProps
      ? { ...baseProps, orientation: baseProps.horizontal ? "horizontal" as const : "vertical" as const, type: C1_EloItem_Type.Button as const }
      : instance.properties?.type === C1_EloItem_Type.Button
        ? instance.properties
        : createDefaultButtonProperties(getButtonProductDefinition(instance.details?.buttonType));
    return {
      id: instance.id,
      ...(instance.tag !== undefined ? { tag: instance.tag } : {}),
      definitionId: hierarchicalDefinitionId,
      ...(description ? { description } : {}),
      ...(instance.count !== undefined ? { count: instance.count } : {}),
      ...(details ? { details } : {}),
      properties,
    };
  }

  const details = cleanDetails(instance.details);
  return {
    id: instance.id,
    ...(instance.tag !== undefined ? { tag: instance.tag } : {}),
    definitionId: hierarchicalDefinitionId,
    ...(description ? { description } : {}),
    consumerType: type,
    ...(instance.count !== undefined ? { count: instance.count } : {}),
    ...(details ? { details } : {}),
  };
}

function mapEloInstances(
  instances: C1_EloItem_Data[],
  definitions: C1_EloItem_Definition[],
  parentDefinitionIds: string[] = [],
  descriptionParts: string[] = [],
  templateContext: DescriptionTemplateContext = {},
): C1_ReportContainer {
  const result = emptyContainer();
  instances.forEach((instance) => {
    const item = mapEloInstance(instance, definitions, parentDefinitionIds, descriptionParts, templateContext);
    if (item) addItem(result, item);
  });
  return result;
}

function sourceGroup(
  id: string,
  name: string,
  sourceType: C1_ReportGroup["sourceType"],
  sourceId: string,
  instance: C1_EloItem_Data | undefined,
  definitions: C1_EloItem_Definition[],
  direction?: number,
  additionalItems: C1_ReportItem[] = [],
  description?: string,
  templateContext: DescriptionTemplateContext = {},
  forceGroup = false,
): C1_ReportItem[] {
  if (!instance) return [];
  const rootDefinitionIds = instance.definitionId ? [instance.definitionId] : [];
  const content = mapEloInstances(
    instance.items,
    definitions,
    rootDefinitionIds,
    description ? [description] : [],
    templateContext,
  );
  additionalItems.forEach((item) => addItem(content, item));
  applySourceMetadata(content, sourceType, sourceId, direction);
  const liftedItem = forceGroup ? undefined : onlyItem(content);
  if (liftedItem) {
    if ("groups" in liftedItem) return [liftedItem];
    return [
      {
        ...liftedItem,
        sourceType,
        sourceId,
        ...(direction !== undefined ? { direction } : {}),
      },
    ];
  }
  if (itemCount(content) === 0) return [];

  return [
    {
      id,
      name,
      sourceType,
      sourceId,
      ...(direction !== undefined ? { direction } : {}),
      ...content,
    },
  ];
}

function markGroupOnlyButton(items: C1_ReportItem[], buttonId: string, belongsToRoomName: string): void {
  items.forEach((item) => {
    if (!("groups" in item)) return;
    item.buttons.forEach((button) => {
      if (button.id !== buttonId) return;
      button.groupDisplayOnly = true;
      button.belongsToRoomName = belongsToRoomName;
    });
    markGroupOnlyButton(item.groups, buttonId, belongsToRoomName);
  });
}

function applySourceMetadata(
  container: C1_ReportContainer,
  sourceType: C1_ReportSourceType,
  sourceId: string,
  direction?: number,
) {
  container.consumers.forEach((item) => {
    item.sourceType = sourceType;
    item.sourceId = sourceId;
    if (direction !== undefined) item.direction = direction;
  });
  container.buttons.forEach((item) => {
    item.sourceType = sourceType;
    item.sourceId = sourceId;
    if (direction !== undefined) item.direction = direction;
  });
  container.groups.forEach((group) => {
    group.sourceType = sourceType;
    group.sourceId = sourceId;
    if (direction !== undefined) group.direction = direction;
    applySourceMetadata(group, sourceType, sourceId, direction);
  });
}

function definitionBaseId(instance: C1_EloItem_Data): string {
  return instance.definitionId.replace(/-\d+$/, "");
}

/** Prepares shared project data for the roombook, bill of materials and button list. */
export function createC1ProjectReportData(project: C1_Project): C1_ProjectReportData {
  return {
    id: project.id,
    tag: "project",
    name: project.getDisplayName(),
    floors: project.floors.map((floor) => {
      const individualRooms = floor.rooms.map((room) => {
        const roomEloContent = mapEloInstances(
          room.eloTree?.items ?? [],
          getRoomEloItemProfile(room.type),
          room.eloTree?.definitionId ? [room.eloTree.definitionId] : [],
          [],
          {
            eloDisplayName: (id: string) => materializedRootForEloOwner(room)?.findItem(id)?.owner?.getDisplayName(),
            buttonBaseProps: (id: string) => (materializedRootForEloOwner(room)?.findItem(id)?.owner as Button | undefined)?.baseProps?.buttonBaseProps,
          },
        );
        applySourceMetadata(roomEloContent, "room", room.id);
        const result: C1_ReportRoom = {
          id: room.id,
          tag: room.tag,
          fullTag: room.owner?.fullTag,
          name: (room.owner?.displayName ?? ""),
          ...roomEloContent,
          sensors: [],
        };

        room.furnitures.forEach((furniture) => {
          const furnitureDescription = (furniture.owner?.displayName ?? "");
          const furnitureItems = sourceGroup(
            furniture.id,
            furnitureDescription,
            "furniture",
            furniture.id,
            furniture.eloTree,
            getEloItemProfileForInstance(furniture.definitionId, furniture.eloTree),
            undefined,
            [],
            furnitureDescription,
            {
              eloDisplayName: (id: string) => materializedRootForEloOwner(furniture)?.findItem(id)?.owner?.getDisplayName(),
              buttonBaseProps: (id: string) => (materializedRootForEloOwner(furniture)?.findItem(id)?.owner as Button | undefined)?.baseProps?.buttonBaseProps,
            },
          );
          furnitureItems.forEach((item) => addItem(result, item));
        });

        room.openings.forEach((opening) => {
          const isWindow = opening.kind === "window";
          const profile = getOpeningEloProfile(opening);
          const definitions = profile.definitions;
          const resolvedOpeningName = (opening.owner?.displayName ?? "");
          const outsideButton =
            !isWindow && opening.doorType === "interior"
              ? opening.eloTree?.items.find((item) => definitionBaseId(item) === "door-internal.button-outside")
              : undefined;
          const openingItems = sourceGroup(
            opening.id,
            resolvedOpeningName,
            opening.kind === "garage-door" ? "door" : opening.kind,
            opening.id,
            opening.doorType === "passage" && opening.partnerRoom && opening.eloTree
              ? { ...opening.eloTree, items: opening.eloTree.items.filter(item => !definitionBaseId(item).includes("-outside-")) }
              : opening.eloTree,
            definitions,
            opening.direction,
            [],
            undefined,
            {
              partnerRoomName: opening.partnerRoomName,
              eloDisplayName: (id: string) => materializedRootForEloOwner(opening)?.findItem(id)?.owner?.getDisplayName(),
              buttonBaseProps: (id: string) => (materializedRootForEloOwner(opening)?.findItem(id)?.owner as Button | undefined)?.baseProps?.buttonBaseProps,
            },
            Boolean(outsideButton),
          );
          if (outsideButton && opening.partnerRoomName) {
            markGroupOnlyButton(openingItems, outsideButton.id, opening.partnerRoomName);
          }
          openingItems.forEach((item) => addItem(result, item));
        });

        return result;
      });

      // Equal room names denote parts of one logical room. Keep the first
      // room's identity and combine all electrical content into that room.
      const rooms: C1_ReportRoom[] = [];
      const roomsByName = new Map<string, C1_ReportRoom>();
      const roomsById = new Map<string, C1_ReportRoom>();
      individualRooms.forEach((room) => {
        const nameKey = room.name.trim().normalize("NFC").toLocaleLowerCase("de");
        const groupedRoom = roomsByName.get(nameKey);
        if (groupedRoom) {
          groupedRoom.consumers.push(...room.consumers);
          groupedRoom.sensors.push(...room.sensors);
          groupedRoom.buttons.push(...room.buttons);
          groupedRoom.groups.push(...room.groups);
          roomsById.set(room.id, groupedRoom);
          return;
        }

        roomsByName.set(nameKey, room);
        roomsById.set(room.id, room);
        rooms.push(room);
      });

      // Door buttons located outside the owning room belong to the room on the
      // opposite side. All report rooms already exist before this second pass.
      floor.rooms.forEach((room) => {
        room.openings.forEach((opening) => {
          if (opening.kind !== "door" || (opening.doorType !== "interior" && opening.doorType !== "passage")) return;

          const openingInstance = opening.eloTree;
          if (!openingInstance) return;
          const partnerRoom = opening.partnerRoom;
          const targetRoom = partnerRoom ? roomsById.get(partnerRoom.id) : undefined;
          const outsideItems = openingInstance.items.filter(
            (item) => definitionBaseId(item) === "door-internal.button-outside" ||
              (opening.doorType === "passage" && definitionBaseId(item).startsWith("passage.") && definitionBaseId(item).includes("-outside-")),
          );
          if (!partnerRoom || !targetRoom || !outsideItems.length) return;

          sourceGroup(
            opening.id,
            (opening.owner?.displayName ?? ""),
            "door",
            opening.id,
            {
              ...openingInstance,
              items: outsideItems,
            },
            getOpeningEloProfile(opening).definitions,
            undefined,
            [],
            undefined,
            {
              partnerRoomName: (partnerRoom.owner?.name ?? ""),
              doorRoomName: (room.owner?.name ?? ""),
              eloDisplayName: (id: string) => materializedRootForEloOwner(opening)?.findItem(id)?.owner?.getDisplayName(),
              buttonBaseProps: (id: string) => (materializedRootForEloOwner(opening)?.findItem(id)?.owner as Button | undefined)?.baseProps?.buttonBaseProps,
            },
          ).forEach((item) => addItem(targetRoom, item));
        });
      });

      rooms.forEach((room) => {
        const liftedGroupItems = collectGroupItems(room.groups);
        room.consumers.push(...liftedGroupItems.consumers);
        room.buttons.push(...liftedGroupItems.buttons);
        room.sensors.push(...room.consumers.filter((item) => item.consumerType === C1_EloItem_Type.Sensor));
        room.consumers = room.consumers.filter((item) => item.consumerType !== C1_EloItem_Type.Sensor);
      });

      return {
        id: floor.id,
        tag: floor.tag,
        fullTag: floor.owner?.fullTag,
        name: (floor.owner?.displayName ?? ""),
        level: floor.level,
        rooms,
      };
    }),
  };
}
